import axios from 'axios';

const getApiUrl = () => {
  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.endsWith('.localhost'))) {
    // When testing on localhost, avoid calling remote production API
    if (process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_URL.includes('digitalfatehabad.com')) {
      return process.env.REACT_APP_API_URL;
    }
    return 'http://localhost:5000/api';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
};

const API_URL = getApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  // For admin routes, prioritize admin token
  // For other routes, try user token first, then admin token
  const isAdminRoute = config.url?.includes('/admin/') || window.location.pathname.startsWith('/admin');

  let token;
  if (isAdminRoute) {
    token = localStorage.getItem('adminToken') || localStorage.getItem('userToken');
  } else {
    token = localStorage.getItem('userToken') || localStorage.getItem('adminToken');
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    // Debug log (only first few chars of token for security)
    if (isBulkOperation) {
      console.log('Bulk operation request:', {
        url: config.url,
        hasToken: !!token,
        tokenLength: token.length,
        tokenPreview: token.substring(0, 20) + '...'
      });
    }
  } else {
    // Only warn if not a public endpoint
    // console.warn('No token found for request:', config.url);
  }

  // If data is FormData, let the browser set Content-Type (multipart/form-data with boundary)
  if (config.data instanceof FormData) {
    if (config.headers) delete config.headers['Content-Type'];
    if (config.headers?.common) delete config.headers.common['Content-Type'];
    if (config.headers?.post) delete config.headers.post['Content-Type'];
    if (config.headers?.put) delete config.headers.put['Content-Type'];
    if (config.headers?.patch) delete config.headers.patch['Content-Type'];
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Track if we're in a bulk operation to prevent premature redirects
let isBulkOperation = false;

// Export function to set bulk operation flag
export const setBulkOperation = (value) => {
  isBulkOperation = value;
  console.log('Bulk operation flag set to:', value);
};

// Export function to get bulk operation flag (for debugging)
export const getBulkOperation = () => isBulkOperation;

// Handle response errors - especially 401 (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token is invalid or expired
      const message = error.response?.data?.message || 'Token is not valid';

      // Log error for debugging
      console.error('Authentication error:', {
        message,
        url: error.config?.url,
        isBulkOperation,
        currentPath: window.location.pathname,
        timestamp: new Date().toISOString()
      });

      // Only clear tokens and redirect if NOT in a bulk operation
      // During bulk operations, let the handler manage errors
      if (!isBulkOperation) {
        console.log('Not in bulk operation - clearing tokens and redirecting');
        // Clear tokens
        localStorage.removeItem('adminToken');
        localStorage.removeItem('userToken');
        localStorage.removeItem('userInfo');

        // Only redirect if we're not already on a login page and not during login request
        const currentPath = window.location.pathname;
        const isLoginRequest = error.config?.url?.includes('/login') || error.config?.url?.includes('/auth/login');

        if (!isLoginRequest && !currentPath.includes('/login') && !currentPath.includes('/admin/login')) {
          // Check if it's an admin route
          if (currentPath.startsWith('/admin')) {
            // Use setTimeout to avoid redirect during render
            setTimeout(() => {
              window.location.href = '/admin/login';
            }, 100);
          } else {
            setTimeout(() => {
              window.location.href = '/login';
            }, 100);
          }
        }
      } else {
        // In bulk operation - just log, don't redirect
        console.log('401 error during bulk operation - will be handled by bulk submit handler. NOT redirecting.');
      }
    }
    return Promise.reject(error);
  }
);

export default api;

